import { useEffect, useState } from "react";
import { Pages } from "./page_list";
import { flows } from "@/ui-only-components/flow_template";
import { Question } from "./quiz-flow/pages/Question";
import { Navigation } from "./quiz-flow/Navigation";
import classNames from "classnames";
import { Statement as Statement230k } from "./quiz-flow/pages/Statement230kPlaces";
import { Statement as TagsStatement } from './quiz-flow/pages/TagsStatement';
import { PreCheckout } from "./quiz-flow/pages/PreCheckout";
import { EmailScreen as EmailScreen1 } from './quiz-flow/pages/EmailScreen1';
import { EmailScreen as EmailScreen2 } from './quiz-flow/pages/EmailScreen2';
import { Statement as SafetyStatement } from './quiz-flow/pages/SafetyRankingStatement';
import { Statement as CommunityStatement } from './quiz-flow/pages/CommunityValidationStatement';
import { Statement as CoverageStatement } from './quiz-flow/pages/GlobalCoverageStatement';
import { Statement as AggregatedStatement } from './quiz-flow/pages/AggregatedInfoStatement';
import { Statement as DietitianStatement } from './quiz-flow/pages/DietitianStatement';
import { Statement as RealtimeStatement } from './quiz-flow/pages/RealtimeUpdatesStatement';
import { Statement as CrossContaminationStatement } from './quiz-flow/pages/CrossContaminationStatement';
import { Statement as TravelStatement } from './quiz-flow/pages/TravelFeaturesStatement';
import { Statement as AtlyIntro } from './quiz-flow/pages/AtlyIntro1';
import { Statement as SafetyRankingExplanation } from './quiz-flow/pages/SafetyRankingExplanation';
import { Statement as ReviewAggregationStatement } from './quiz-flow/pages/ReviewAggregationStatement';
import { Statement as InformationUpdateStatement } from './quiz-flow/pages/InformationUpdateStatement';
import { Statement as SafetyCommitmentStatement } from './quiz-flow/pages/SafetyCommitmentStatement';
import { Statement as Statement230kPlaces2 } from './quiz-flow/pages/Statement230kPlaces2';
import { Statement as Loading1 } from './quiz-flow/pages/Loading1';
import { Statement as PerfectFit1 } from './quiz-flow/pages/PerfectFit1';
import { PreCheckoutImproved } from './quiz-flow/pages/PreCheckoutImproved';
import { AprilPricingPage } from './quiz-flow/pages/AprilPricingPage';
import { PaymentPage } from './quiz-flow/pages/PaymentPage';
import { LoadingImproved } from './quiz-flow/pages/LoadingImproved';
import { isEmailValid } from "@/utils/email";
import { useMemo } from "react";
import { QuizStripeEnricher } from "./QuizStripeEnricher";
import { InAppQuizResultsScreen } from "@/features/quiz/InAppQuizResultsScreen";
import { StripeContextProvider } from "@/context/StripeContextProvider";
import { useStripeInit } from "@/hooks/useStripeInit";

export const FullScreenQuizFlow = ({
    isFullPageSliderOpen,
    setIsFullPageSliderOpen,
    formSubmit,
    trackAction,
    trackView,
    flowVersion = "flow_a",
    flowPages,
    locationData,
    noCloseButton = false,
    clientSecret,
    pricePerMonth,
    customerUpdate,
    testUser,
    skipToCheckout,
    stripeData,
    withTrial,
    branchLink,
    userId,
    paymentVariation,
}) => {
    const [currentPageIndex, setCurrentPageIndex] = useState(0);
    const [answerHistory, setAnswerHistory] = useState([]);
    const [isTransitioning, setIsTransitioning] = useState(false);
    const [transitionDirection, setTransitionDirection] = useState(null);
    const [showNavigation, setShowNavigation] = useState(false);
    const [isVisible, setIsVisible] = useState(false);
    const [showHeaderElements, setShowHeaderElements] = useState(false);
    const [userEmail, setUserEmail] = useState("");

    const currentPageName = flowPages[currentPageIndex];
    const totalPages = flowPages.length;
    const currentPage = Pages[currentPageName];

    // Reset state variables when the flow is reopened
    useEffect(() => {
        if (isFullPageSliderOpen) {
            setCurrentPageIndex(0);
            setAnswerHistory([]);
            setShowNavigation(true);
            setIsVisible(true);
        } else {
            setIsVisible(false);
        }
    }, [isFullPageSliderOpen]);

    useEffect(() => {
        if (isFullPageSliderOpen && skipToCheckout) {
            setTimeout(() => {
                setTransitionDirection("forward");
                setIsTransitioning(true);
                setCurrentPageIndex(totalPages - 2);
                setIsTransitioning(false);
            }, 500);
        }
    }, [isFullPageSliderOpen, skipToCheckout, totalPages]);

    // Track page views
    useEffect(() => {
        if (isFullPageSliderOpen && currentPage) {
            trackView({
                page_name: currentPageName,
                page_type: currentPage.type,
                answers: answerHistory[currentPageIndex] || null,
                flow_version: flowVersion,
            });
        }
    }, [currentPageIndex, isFullPageSliderOpen]);

    // Handle header elements visibility
    useEffect(() => {
        if (!currentPage) return;
        setShowHeaderElements(
            currentPage.type !== 'Statement' && currentPage.type !== 'InAppQuizResultsScreen'
        );
    }, [currentPage?.type]);

    const handleAnswer = async (answer, event) => {
        if (!currentPage) {
            console.error('Current page is undefined');
            return;
        }

        // Track the answer
        trackAction({
            page_name: currentPageName,
            action: "Question Answered",
            value_submitted: Array.isArray(answer)
                ? answer.map((a) => a.text)
                : [answer.text],
            flow_version: flowVersion,
        });

        if (currentPageName === 'EmailScreen1' || currentPageName === 'EmailScreen2') {
            const email = answer.text;
            if (isEmailValid(email)) {
                setUserEmail(email);
            }
        }

        // Update answer history
        setAnswerHistory((prev) => {
            const newHistory = [...prev];
            newHistory[currentPageIndex] = answer;
            return newHistory;
        });

        // Handle page transition
        if (currentPageIndex < totalPages - 1) {
            setTransitionDirection("forward");
            setIsTransitioning(true);

            // Use Promise-based timeout for better control
            await new Promise(resolve => setTimeout(resolve, 400));
            setCurrentPageIndex(prev => prev + 1);
            await new Promise(resolve => setTimeout(resolve, 50));
            setIsTransitioning(false);
        } else {
            // Handle form submission at the end
            formSubmit(answerHistory);
            setIsFullPageSliderOpen(false);
        }
    };

    const handleBack = async () => {
        if (currentPageIndex > 0) {
            setTransitionDirection("back");
            setIsTransitioning(true);
            await new Promise(resolve => setTimeout(resolve, 300));
            setCurrentPageIndex(prev => prev - 1);
            setIsTransitioning(false);
        } else {
            // Handle closing the flow
            setIsVisible(false);
            await new Promise(resolve => setTimeout(resolve, 500));
            setIsFullPageSliderOpen(false);
            setCurrentPageIndex(0);
            setAnswerHistory([]);
            setShowNavigation(false);
        }
    };

    const handleSkip = async () => {
        if (currentPageIndex >= totalPages - 1) {
            console.error('No more pages to skip to');
            return;
        }

        trackAction({
            page_name: currentPageName,
            action: "Skip Clicked",
            value_submitted: null,
            flow_version: flowVersion,
        });

        // Add a skipped entry to answer history
        setAnswerHistory(prev => {
            const newHistory = [...prev];
            newHistory[currentPageIndex] = { skipped: true };
            return newHistory;
        });

        setTransitionDirection("forward");
        setIsTransitioning(true);
        await new Promise(resolve => setTimeout(resolve, 400));
        setCurrentPageIndex(prev => prev + 1);
        setIsTransitioning(false);
    };

    const shouldShowSkip = () => {
        if (!currentPage) return false;
        return currentPage.type === 'Question' && currentPage.allowSkip !== false;
    };

    const shouldShowNavigation = () => {
        if (!currentPage) return false;
        if (currentPageIndex === totalPages - 1) return false;
        return ['Question', 'PreCheckout', 'Email'].includes(currentPage.type);
    };

    const shouldShowProgressDots = () => {
        return currentPage?.type !== 'PreCheckout';
    };

    // Update showNavigation when currentPage changes
    useEffect(() => {
        setShowNavigation(shouldShowNavigation());
    }, [currentPage]);

    const bgColor = useMemo(() => {
        return currentPage?.bgColor ||
            "linear-gradient(80deg, rgba(240, 30, 111, 0.05) 12.92%, rgba(248, 94, 74, 0.05) 94.79%), #FFF";
    }, [currentPage]);

    const paymentData = useStripeInit(paymentVariation, true);

    if (!currentPageName || !currentPage) {
        throw new Error('Current page is undefined');
    }

    if (!isFullPageSliderOpen)
        return null;

    console.log({ showHeaderElements, showNavigation, currentPage });

    return (
        <>
            <QuizStripeEnricher />
            <div
                id="full-screen-quiz-flow"
                style={{ background: bgColor }}
                className={classNames(
                    "fixed inset-0 z-50",
                    {
                        "pt-0": currentPage.hideHeader,
                        "pt-20": !currentPage.hideHeader && !showNavigation, // Just logo
                        "pt-28": !currentPage.hideHeader && showNavigation,  // Logo + navigation
                    },
                    "transition-opacity duration-300",
                    {
                        "opacity-0 translate-y-full pointer-events-none": !isVisible,
                        "opacity-100 translate-y-0": isVisible,
                        "pointer-events-none": isTransitioning,
                    }
                )}>
                {/* Logo Header */}
                <div className={classNames(
                    "absolute top-6 left-1/2 transform -translate-x-1/2",
                    "transition-opacity duration-300",
                    {
                        "opacity-0": !showHeaderElements,
                        "opacity-100": showHeaderElements,
                        "hidden": currentPage.hideHeader,
                    }
                )}>
                    <img
                        src="/assets/Logo-lockup-small.png"
                        alt="Atly Logo"
                        className="h-8 w-auto"
                    />
                </div>

                {/* Navigation */}
                {showNavigation && currentPage && (
                    <div className={classNames(
                        "transition-opacity duration-300",
                        {
                            "opacity-0": !showHeaderElements,
                            "opacity-100": showHeaderElements,
                        }
                    )}>
                        <Navigation
                            onBack={handleBack}
                            showBack={true}
                            noCloseButton={noCloseButton}
                            onSkip={handleSkip}
                            showSkip={shouldShowSkip()}
                            totalSteps={totalPages}
                            currentStep={currentPageIndex + 1}
                            isFirstQuestion={currentPageIndex === 0}
                            showProgressDots={shouldShowProgressDots()}
                            className="mt-8"
                        />
                    </div>
                )}

                {/* Page Content */}
                {currentPage && (
                    <div key={currentPageIndex}>
                        {currentPage.type === "Question" && (
                            <Question
                                data={currentPage}
                                onAnswer={handleAnswer}
                                flowVersion={flowVersion}
                                previousAnswer={answerHistory[currentPageIndex]}
                                isTransitioning={isTransitioning}
                                direction={transitionDirection}
                                locationData={locationData}
                            />
                        )}
                        {currentPage.type === "Statement" && (
                            <>
                                {currentPageName === 'Statement230kPlaces' && (
                                    <Statement230k
                                        data={currentPage}
                                        onAnswer={handleAnswer}
                                        isTransitioning={isTransitioning}
                                        direction={transitionDirection}
                                    />
                                )}
                                {currentPageName === 'TagsStatement' && (
                                    <TagsStatement
                                        data={currentPage}
                                        onAnswer={handleAnswer}
                                        isTransitioning={isTransitioning}
                                        direction={transitionDirection} />
                                )}
                                {currentPageName === 'SafetyRankingStatement' && (
                                    <SafetyStatement
                                        data={currentPage}
                                        onAnswer={handleAnswer}
                                        isTransitioning={isTransitioning}
                                        direction={transitionDirection}
                                    />
                                )}
                                {currentPageName === 'CommunityValidationStatement' && (
                                    <CommunityStatement
                                        data={currentPage}
                                        onAnswer={handleAnswer}
                                        isTransitioning={isTransitioning}
                                        direction={transitionDirection}
                                        previousAnswer={answerHistory[currentPageIndex - 1]}
                                    />
                                )}
                                {currentPageName === 'GlobalCoverageStatement' && (
                                    <CoverageStatement
                                        data={currentPage}
                                        onAnswer={handleAnswer}
                                        isTransitioning={isTransitioning}
                                        direction={transitionDirection}
                                    />
                                )}
                                {currentPageName === 'AggregatedInfoStatement' && (
                                    <AggregatedStatement
                                        data={currentPage}
                                        onAnswer={handleAnswer}
                                        isTransitioning={isTransitioning}
                                        direction={transitionDirection}
                                    />
                                )}
                                {currentPageName === 'DietitianStatement' && (
                                    <DietitianStatement
                                        data={currentPage}
                                        onAnswer={handleAnswer}
                                        isTransitioning={isTransitioning}
                                        direction={transitionDirection}
                                    />
                                )}
                                {currentPageName === 'RealtimeUpdatesStatement' && (
                                    <RealtimeStatement
                                        data={currentPage}
                                        onAnswer={handleAnswer}
                                        isTransitioning={isTransitioning}
                                        direction={transitionDirection}
                                    />
                                )}
                                {currentPageName === 'CrossContaminationStatement' && (
                                    <CrossContaminationStatement
                                        data={currentPage}
                                        onAnswer={handleAnswer}
                                        isTransitioning={isTransitioning}
                                        direction={transitionDirection}
                                    />
                                )}
                                {currentPageName === 'TravelFeaturesStatement' && (
                                    <TravelStatement
                                        data={currentPage}
                                        onAnswer={handleAnswer}
                                        isTransitioning={isTransitioning}
                                        direction={transitionDirection}
                                    />
                                )}
                                {currentPageName === 'AtlyIntro1' && (
                                    <AtlyIntro
                                        data={currentPage}
                                        onAnswer={handleAnswer}
                                        isTransitioning={isTransitioning}
                                        direction={transitionDirection}
                                    />
                                )}
                                {currentPageName === 'SafetyRankingExplanation' && (
                                    <SafetyRankingExplanation
                                        data={currentPage}
                                        onAnswer={handleAnswer}
                                        isTransitioning={isTransitioning}
                                        direction={transitionDirection}
                                    />
                                )}
                                {currentPageName === 'ReviewAggregationStatement' && (
                                    <ReviewAggregationStatement
                                        data={currentPage}
                                        onAnswer={handleAnswer}
                                        isTransitioning={isTransitioning}
                                        direction={transitionDirection}
                                    />
                                )}
                                {currentPageName === 'InformationUpdateStatement' && (
                                    <InformationUpdateStatement
                                        data={currentPage}
                                        onAnswer={handleAnswer}
                                        isTransitioning={isTransitioning}
                                        direction={transitionDirection}
                                    />
                                )}
                                {currentPageName === 'Statement230kPlaces2' && (
                                    <Statement230kPlaces2
                                        data={currentPage}
                                        onAnswer={handleAnswer}
                                        isTransitioning={isTransitioning}
                                        direction={transitionDirection}
                                    />
                                )}
                                {currentPageName === 'SafetyCommitmentStatement' && (
                                    <SafetyCommitmentStatement
                                        data={currentPage}
                                        onAnswer={handleAnswer}
                                        isTransitioning={isTransitioning}
                                        direction={transitionDirection}
                                    />
                                )}
                                {currentPageName === 'Loading1' && (
                                    <Loading1
                                        data={currentPage}
                                        onAnswer={handleAnswer}
                                        isTransitioning={isTransitioning}
                                        direction={transitionDirection}
                                    />
                                )}
                                {currentPageName === 'PerfectFit1' && (
                                    <PerfectFit1
                                        data={currentPage}
                                        onAnswer={handleAnswer}
                                        isTransitioning={isTransitioning}
                                        direction={transitionDirection}
                                    />
                                )}
                                {currentPageName === 'PlacesStatement' && (
                                    <PlacesStatement
                                        data={currentPage}
                                        onAnswer={handleAnswer}
                                        isTransitioning={isTransitioning}
                                        direction={transitionDirection}
                                    />
                                )}
                                {currentPageName === 'BiggestStruggles1' && (
                                    <BiggestStruggles1
                                        data={currentPage}
                                        onAnswer={handleAnswer}
                                        isTransitioning={isTransitioning}
                                        direction={transitionDirection}
                                    />
                                )}
                                {currentPageName === 'WhereDoYouWantPlaces' && (
                                    <WhereDoYouWantPlaces
                                        data={currentPage}
                                        onAnswer={handleAnswer}
                                        isTransitioning={isTransitioning}
                                        direction={transitionDirection}
                                    />
                                )}
                                {currentPageName === 'InformationSources' && (
                                    <InformationSources
                                        data={currentPage}
                                        onAnswer={handleAnswer}
                                        isTransitioning={isTransitioning}
                                        direction={transitionDirection}
                                    />
                                )}
                                {currentPageName === 'InformationAccuracyQuestion' && (
                                    <InformationAccuracyQuestion
                                        data={currentPage}
                                        onAnswer={handleAnswer}
                                        isTransitioning={isTransitioning}
                                        direction={transitionDirection}
                                    />
                                )}
                                {currentPageName === 'FeaturePreference' && (
                                    <FeaturePreference
                                        data={currentPage}
                                        onAnswer={handleAnswer}
                                        isTransitioning={isTransitioning}
                                        direction={transitionDirection}
                                    />
                                )}
                            </>
                        )}
                        <StripeContextProvider
                            {...stripeData}
                            userEmail={userEmail}
                            withTrial={withTrial}
                            branchLink={branchLink}
                            userId={userId}>
                            {currentPage.type === "PreCheckout" && (
                                <PreCheckout
                                    data={currentPage}
                                    onAnswer={handleAnswer}
                                    formSubmit={formSubmit}
                                    isTransitioning={isTransitioning}
                                    direction={transitionDirection}
                                    clientSecret={clientSecret}
                                    pricePerMonth={pricePerMonth}
                                    customerUpdate={customerUpdate}
                                    userEmail={userEmail}
                                    onEmailChange={setUserEmail}
                                />
                            )}
                        </StripeContextProvider>
                        {currentPage.type === "PreCheckoutImproved" && (
                            <PreCheckoutImproved
                                userEmail={userEmail}
                                onEmailChange={setUserEmail}
                                onAnswer={handleAnswer}
                            />
                        )}
                        {currentPage.type === "AprilPricingPage" && (
                            <AprilPricingPage
                                testUser={testUser}
                                onAnswer={handleAnswer}
                            />
                        )}
                        {currentPage.type === "PaymentPageRedirect" && (
                            <PaymentPage
                                userEmail={userEmail}
                                paymentVariation={paymentVariation}
                                paymentData={paymentData}
                                locationData={locationData} />
                        )}
                        {currentPage.type === "InAppQuizResultsScreen" && (
                            <InAppQuizResultsScreen
                                onAnswer={handleAnswer}
                                isTransitioning={isTransitioning}
                            />
                        )}
                        {currentPage.type === "LoadingImproved" && (
                            <LoadingImproved onAnswer={handleAnswer} />
                        )}
                        {currentPage.type === 'Email' && (
                            <>
                                {currentPageName === 'EmailScreen3' ? (
                                    <EmailScreen2
                                        data={{
                                            ...currentPage,
                                            savedEmail: answerHistory[currentPageIndex]?.text
                                        }}
                                        onAnswer={handleAnswer}
                                        isTransitioning={isTransitioning}
                                        direction={transitionDirection}
                                    />
                                ) : (
                                    <EmailScreen1
                                        data={{
                                            ...currentPage,
                                            savedEmail: answerHistory[currentPageIndex]?.text
                                        }}
                                        onAnswer={handleAnswer}
                                        isTransitioning={isTransitioning}
                                        direction={transitionDirection}
                                    />
                                )}
                            </>
                        )}
                    </div>
                )}
            </div>
        </>
    );
};
